// The Storefront Styles list, made available to the whole pages-editor tree.
//
// Two consumers, two mechanisms:
//  * React components (the "Style" pickers in ElementStyleControls, the Styles panel) read the
//    list through `useStorefrontStyles()`.
//  * The pure preview builders in styleControls.tsx (elementStyleFrom / cardStyleFrom / …) are
//    not components, so they read the module registry in stylesConfig.ts instead. The provider
//    keeps that registry in step with the context value, and because it re-renders its subtree
//    whenever the list changes, the builders re-run against the new list on the same pass —
//    which is what makes unsaved Styles-panel edits show up in the preview instantly.

import React from 'react';
import { IStorefrontStyle } from '../../../models/Storefront';
import { setActiveStorefrontStyles } from './stylesConfig';

export interface IStorefrontStylesContext {
    /** Server styles merged with any unsaved Styles-panel drafts (own storefront + org globals). */
    styles: IStorefrontStyle[];
    /** The storefront being edited — the scope new styles are created in. */
    storefrontId?: string;
    /** False when the signed-in user can't administer styles (pickers render read-only/hidden). */
    canEdit?: boolean;
}

const EMPTY: IStorefrontStyle[] = [];

const StorefrontStylesContext = React.createContext<IStorefrontStylesContext>({ styles: EMPTY, canEdit: false });

export const useStorefrontStyles = (): IStorefrontStylesContext => React.useContext(StorefrontStylesContext);

export const StorefrontStylesProvider: React.FC<IStorefrontStylesContext> = ({ styles, storefrontId, canEdit, children }) => {
    // Published during render (not in an effect) so the builders called by children — which render
    // after this function returns — already see the current list on the very first paint.
    setActiveStorefrontStyles(styles);
    // Clear on unmount so a screen outside the pages editor can't resolve a ref against a stale list.
    React.useEffect(() => () => setActiveStorefrontStyles([]), []);
    const value = React.useMemo(() => ({ styles, storefrontId, canEdit }), [styles, storefrontId, canEdit]);
    return <StorefrontStylesContext.Provider value={value}>{children}</StorefrontStylesContext.Provider>;
};

export default StorefrontStylesContext;
